<template>
  <aside
    v-if="groupIdRef"
    :class="['conversation-context', { 'conversation-context--sheet': inSheet }]"
    :aria-label="t('chat.context.label')"
    data-test-id="chat-context"
  >
    <section class="conversation-context__section" aria-labelledby="chat-context-next-session">
      <h3 id="chat-context-next-session" class="conversation-context__eyebrow">{{ t('chat.context.nextSession') }}</h3>
      <div v-if="nextSessionRef" class="conversation-context__card" data-test-id="chat-context-next-session">
        <p class="conversation-context__card-title">
          <time :datetime="nextSessionRef.from">{{ getDate(nextSessionRef.from) }}</time>
        </p>
        <p class="conversation-context__meta">
          {{ getDurationRange(nextSessionRef.from, nextSessionRef.to) }} ·
          {{ nextSessionRef.location === 'visio' ? t('session.type.online') : nextSessionRef.location }}
        </p>
        <Button v-if="canJoinNextSessionRef" type="button" variant="outline" size="sm" @click="joinNextSession">
          <Video class="size-4" aria-hidden="true" />
          {{ t('widget.nextSession.join') }}
        </Button>
      </div>
      <p v-else class="conversation-context__meta">{{ t('chat.context.noSession') }}</p>
    </section>

    <section
      v-if="objectivesRef.length > 0"
      class="conversation-context__section"
      aria-labelledby="chat-context-progress"
    >
      <div class="conversation-context__row">
        <h3 id="chat-context-progress" class="conversation-context__eyebrow">{{ t('chat.context.progress') }}</h3>
        <span class="conversation-context__count">{{ progressRef.done }} / {{ progressRef.expected }}</span>
      </div>
      <div
        class="conversation-context__bar"
        role="progressbar"
        aria-labelledby="chat-context-progress"
        aria-valuemin="0"
        :aria-valuemax="progressRef.expected"
        :aria-valuenow="progressRef.done"
      >
        <span :style="{ width: `${progressRef.percent}%` }"></span>
      </div>
      <p class="conversation-context__meta">
        {{ t('chat.context.sessionsDone', { done: progressRef.done, expected: progressRef.expected }) }}
      </p>
    </section>

    <section
      v-if="objectivesRef.length > 0"
      class="conversation-context__section"
      aria-labelledby="chat-context-objectives"
    >
      <h3 id="chat-context-objectives" class="conversation-context__eyebrow">{{ t('chat.context.objectives') }}</h3>
      <ul class="conversation-context__objectives">
        <li
          v-for="objective in objectiveItemsRef"
          :key="objective.id"
          :class="`conversation-context__objective conversation-context__objective--${objective.status}`"
        >
          <CircleCheck v-if="objective.status === 'done'" class="size-4 shrink-0" aria-hidden="true" />
          <Clock v-else-if="objective.status === 'current'" class="size-4 shrink-0" aria-hidden="true" />
          <Circle v-else class="size-4 shrink-0" aria-hidden="true" />
          <span>
            {{ objective.title }}
            <span class="sr-only">({{ t(`chat.context.objective.${objective.status}`) }})</span>
          </span>
        </li>
      </ul>
    </section>
    <section
      v-if="documentsRef.length > 0"
      class="conversation-context__section"
      aria-labelledby="chat-context-documents"
      data-test-id="chat-context-documents"
    >
      <div class="conversation-context__row">
        <h3 id="chat-context-documents" class="conversation-context__eyebrow">{{ t('chat.context.documents') }}</h3>
        <span class="conversation-context__count">{{ documentsRef.length }}</span>
      </div>
      <ul v-if="imagesRef.length > 0" class="conversation-context__thumbs">
        <li v-for="image in previewImagesRef" :key="image.id">
          <button
            type="button"
            class="conversation-context__thumb"
            :aria-label="t('chat.documents.open', { name: image.name })"
            @click="openChatFile(image, image.ownerId)"
          >
            <img
              v-if="image.signedUrl && !failedImagesRef.has(image.id)"
              :src="image.signedUrl"
              alt=""
              loading="lazy"
              @error="failedImagesRef.add(image.id)"
            />
            <ImageIcon v-else class="size-4" aria-hidden="true" />
          </button>
        </li>
        <li v-if="hiddenImagesCountRef > 0">
          <button
            type="button"
            class="conversation-context__thumb conversation-context__thumb--more"
            :aria-label="t('chat.context.moreImages', { count: imagesRef.length })"
            @click="openDocuments('images')"
          >
            +{{ hiddenImagesCountRef }}
          </button>
        </li>
      </ul>
      <ul v-if="previewFilesRef.length > 0" class="conversation-context__documents">
        <li v-for="document in previewFilesRef" :key="document.id">
          <button
            type="button"
            class="conversation-context__document"
            @click="openChatFile(document, document.ownerId)"
          >
            <span class="conversation-context__ext" aria-hidden="true">{{ document.ext.toUpperCase() }}</span>
            <span class="conversation-context__document-text">
              <span class="conversation-context__document-name">{{ document.name }}</span>
              <span class="conversation-context__meta">{{ document.author }}, {{ document.dateLabel }}</span>
            </span>
          </button>
        </li>
      </ul>
      <Button
        v-if="documentsRef.length > previewImagesRef.length + previewFilesRef.length"
        type="button"
        variant="link"
        size="sm"
        class="self-start px-0"
        data-test-id="chat-context-documents-all"
        @click="openDocuments('all')"
      >
        {{ t('chat.context.documentsAll', { count: documentsRef.length }) }}
      </Button>
      <ConversationDocumentsSheet
        v-model:open="isDocumentsOpenRef"
        :documents="documentsRef"
        :conversation-name="conversationRef?.groupName"
        :initial-filter="documentsFilterRef"
      />
    </section>
  </aside>
</template>

<script setup lang="ts">
import { Circle, CircleCheck, Clock, ImageIcon, Video } from 'lucide-vue-next'
import { computed, reactive, ref } from 'vue'

import { Button } from '@/components/ui/button'
import ConversationDocumentsSheet from '@/features/chat/components/conversation-context/conversation-documents-sheet.vue'
import useConversation from '@/features/chat/hooks/use-conversation.hook'
import { getChatDocuments } from '@/features/chat/utils/chat-documents'
import { openChatFile } from '@/features/chat/utils/open-chat-file'
import useSessions from '@/features/sessions/hooks/use-sessions.hook'
import { getPlannedSessions, sortSessionsByDate } from '@/features/sessions/utils'
import useDashboardData from '@/features/ui/pages/dashboard/hooks/use-dashboard-data.hook'
import useRouter from '@/hooks/use-router.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'
import { getDate, getDurationRange } from '@/utils/dates'

// inSheet : rendu dans le tiroir mobile plutot qu'en troisieme colonne
defineProps<{ inSheet?: boolean }>()

const { t } = useI18n()

// miniature introuvable ou expiree : on affiche l'icone a la place
const failedImagesRef = reactive(new Set<string>())
const router = useRouter()

const currentRouteRef = selectGetCurrentRoute(routeStore)
const conversationIdRef = computed(() => (currentRouteRef.value?.params?.roomId as string) ?? null)
const { conversationRef } = useConversation(conversationIdRef)

// le panneau ne concerne que les conversations de binôme / groupe
const groupIdRef = computed(() => conversationRef.value?.groupId ?? null)

// prochaine session planifiée du groupe
const { sessionsRef } = useSessions()
const nextSessionRef = computed(() => {
  if (!groupIdRef.value) return null
  const upcoming = sessionsRef.value
    .filter(getPlannedSessions)
    .filter((session) => session.mentoringGroupId === groupIdRef.value)
    .filter((session) => new Date(session.to).getTime() > Date.now())
    .sort(sortSessionsByDate)
  return upcoming[0] ?? null
})

// même règle que la prochaine session de la navigation : rejoindre à partir de 2 h avant
const canJoinNextSessionRef = computed(() => {
  if (!nextSessionRef.value) return false
  const now = dayjsClient()
  const start = dayjsClient(nextSessionRef.value.from)
  const end = dayjsClient(nextSessionRef.value.to)
  const inProgress = now.isAfter(start) && now.isBefore(end)
  return inProgress || start.diff(now, 'hours') <= 2
})

function joinNextSession() {
  if (!nextSessionRef.value) return
  router?.push({ query: { ...router.currentRoute.query, meetingRoomId: nextSessionRef.value.meetingRoomId } })
}

// documents echanges : apercu (3 images, 3 fichiers) et panneau complet
const PREVIEW_COUNT = 3
const documentsRef = computed(() =>
  getChatDocuments(conversationRef.value, {
    today: t('chat.conversation.today'),
    yesterday: t('chat.conversation.yesterday'),
  }),
)
const imagesRef = computed(() => documentsRef.value.filter((document) => document.isImage))
const previewImagesRef = computed(() => imagesRef.value.slice(0, PREVIEW_COUNT))
const hiddenImagesCountRef = computed(() => imagesRef.value.length - previewImagesRef.value.length)
const previewFilesRef = computed(() =>
  documentsRef.value.filter((document) => !document.isImage).slice(0, PREVIEW_COUNT),
)
const isDocumentsOpenRef = ref(false)
const documentsFilterRef = ref<'all' | 'images' | 'files'>('all')
function openDocuments(filter: 'all' | 'images' | 'files') {
  documentsFilterRef.value = filter
  isDocumentsOpenRef.value = true
}

// parcours et objectifs du groupe
const { objectivesRef } = useDashboardData({ selectedGroupIdRef: groupIdRef })

const progressRef = computed(() => {
  const expected = objectivesRef.value.reduce((sum, objective) => sum + objective.expectedSessionCount, 0)
  const done = objectivesRef.value.reduce(
    (sum, objective) => sum + Math.min(objective.completedSessionsCount, objective.expectedSessionCount),
    0,
  )
  return { done, expected, percent: expected > 0 ? Math.round((done / expected) * 100) : 0 }
})

const objectiveItemsRef = computed(() => {
  const currentIndex = objectivesRef.value.findIndex(
    (objective) => objective.completedSessionsCount < objective.expectedSessionCount,
  )
  return [...objectivesRef.value]
    .sort((a, b) => a.order - b.order)
    .map((objective, index) => ({
      id: objective.id,
      title: objective.title,
      status:
        objective.completedSessionsCount >= objective.expectedSessionCount
          ? ('done' as const)
          : index === currentIndex
            ? ('current' as const)
            : ('todo' as const),
    }))
})
</script>

<style lang="scss">
@import './conversation-context.scss';
</style>
